import { useState, useEffect, useCallback } from 'react';
import useDebounce from './useDebounce';
import useEmployeesStoreRepository from '../store/employeesStoreRepository';
import { ORGANIZATIONAL_STATUSES } from '../constants/organizationalStatuses';
import { EMPLOYEE_REPORTING_SCOPE } from '../constants/employeeReportingScope';

const useEmployeesRepositorySearch = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [reportingScope, setReportingScope] = useState(EMPLOYEE_REPORTING_SCOPE.ALL);
  
  const debouncedSearchTerm = useDebounce(searchInputValue, 500);
  
  const {
    searchTerm,
    setSearchTerm,
    setScrollPosition,
    setLastCardId,
    setIsReturningFromDetails,
    setPositionIds,
    setGradeIds,
    setProjectIds,
    setOrganizationalStatus,
    setClusterId,
    setReportingScope: setStoreReportingScope,
    uiSearchInput,
    setUISearchInput,
    setSelectedPositions: setStoreSelectedPositions,
    setSelectedGrades: setStoreSelectedGrades,
    setSelectedProjects: setStoreSelectedProjects,
    setSelectedStatuses: setStoreSelectedStatuses,
    setSelectedCluster: setStoreSelectedCluster
  } = useEmployeesStoreRepository();

  // Synchronize UI with store on mount and when uiSearchInput changes (like CV Repository)
  useEffect(() => {
    // Only sync from store to UI input when mounting or when store UI input explicitly changes
    if (uiSearchInput !== undefined && uiSearchInput !== searchInputValue) {
      setSearchInputValue(uiSearchInput);
    }
  }, [uiSearchInput]);

  // Keep the store's uiSearchInput in sync with our local state (like CV Repository)
  useEffect(() => {
    setUISearchInput(searchInputValue);
  }, [searchInputValue, setUISearchInput]);
  
  // Create a wrapper for setting the search term that handles loading state (like CV Repository)
  const handleSearchTermChange = useCallback((term) => {
    // When term is empty and searchTerm is also empty, don't do anything
    if (term === '' && searchTerm === '') return;
    
    // Reset state is handled by the store's setSearchTerm implementation
    setSearchTerm(term);
  }, [searchTerm, setSearchTerm]);

  // Handle position change (like CV Repository)
  useEffect(() => {
    // Reset scroll position when positions change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update position IDs in store
    const positionIds = selectedPositions.length > 0 
      ? selectedPositions.join(',') // positions are already IDs
      : '';
    
    setPositionIds(positionIds);
  }, [selectedPositions, setPositionIds, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle grade change
  useEffect(() => {
    // Reset scroll position when grades change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update grade IDs in store
    const gradeIds = selectedGrades.length > 0 
      ? selectedGrades.join(',') // grades are already IDs
      : '';
    
    setGradeIds(gradeIds);
  }, [selectedGrades, setGradeIds, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle project change
  useEffect(() => {
    // Reset scroll position when projects change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update project IDs in store
    const projectIds = selectedProjects.length > 0 
      ? selectedProjects.join(',') // projects are already IDs
      : '';
    
    setProjectIds(projectIds);
  }, [selectedProjects, setProjectIds, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle organizational status change
  useEffect(() => {
    // Reset scroll position when statuses change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update organizational status in store
    const statusString = selectedStatuses.length > 0 ? selectedStatuses.join(',') : '';
    setOrganizationalStatus(statusString);
  }, [selectedStatuses, setOrganizationalStatus, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle cluster change
  useEffect(() => {
    // Reset scroll position when cluster changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update cluster in store
    setClusterId(selectedCluster);
  }, [selectedCluster, setClusterId, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle reportingScope change
  useEffect(() => {
    // Reset scroll position when reportingScope changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update reportingScope in store
    setStoreReportingScope(reportingScope);
  }, [reportingScope, setStoreReportingScope, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle search input with debounce (like CV Repository)
  useEffect(() => {
    // Skip the initial render when both terms are empty
    if (debouncedSearchTerm === '' && searchTerm === '' && searchInputValue === '') {
      return;
    }
    
    // Only proceed if the debounced term has actually changed
    if (debouncedSearchTerm === searchTerm) {
      return;
    }
    
    // Reset scroll position when actively searching
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Short delay to ensure UI updates before fetch operation
    const timer = setTimeout(() => {
      handleSearchTermChange(debouncedSearchTerm);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [debouncedSearchTerm, searchTerm, searchInputValue, handleSearchTermChange, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Method to clear search filters (like CV Repository)
  const clearSearchFilters = useCallback(() => {
    setSearchInputValue('');
    setSearchTerm('');
    setSelectedPositions([]);
    setSelectedGrades([]);
    setSelectedProjects([]);
    setSelectedStatuses([ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING]);
    setSelectedCluster('');
    setReportingScope(EMPLOYEE_REPORTING_SCOPE.ALL);
    // Also update store state
    setStoreSelectedPositions([]);
    setStoreSelectedGrades([]);
    setStoreSelectedProjects([]);
    setStoreSelectedStatuses([ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING]);
    setStoreSelectedCluster('');
  }, [setSearchTerm, setStoreSelectedPositions, setStoreSelectedGrades, setStoreSelectedProjects, setStoreSelectedStatuses, setStoreSelectedCluster]);

  return {
    // Values
    searchInputValue,
    selectedPositions,
    selectedGrades,
    selectedProjects,
    selectedStatuses,
    selectedCluster,
    reportingScope,
    // Handlers (simple setters like CV Repository)
    setSearchInputValue,
    setSelectedPositions,
    setSelectedGrades,
    setSelectedProjects,
    setSelectedStatuses,
    setSelectedCluster,
    setReportingScope,
    clearSearchFilters,
  };
};

export default useEmployeesRepositorySearch;